<template lang='pug'>
  div
    bionic-metrics-page-loading(v-if='loading')
    v-container(fluid v-if='!loading && loadTest')
      v-card.mb-2
        v-tabs(v-model='tab')
          v-tab(key='details') Details
          v-tab(key='target') Target/Inputs
          v-tab(key='concurrency') Concurrency
          v-tab(key='tests') Tests/Counts
        v-tabs-items(v-model='tab')
          v-tab-item(key='details')
            v-card(flat tile)
              v-card-text
                v-text-field(label='Name' @input='triggerSave' v-model='loadTest.name' type='text' required)
                v-textarea(outlined label='Description' @input='triggerSave' v-model='loadTest.description' type='text' auto-grow rows='3')
          v-tab-item(key='target')
            v-card(flat tile)
              v-card-text
                v-text-field(label='Base URL'  @input='triggerSave' placeholder='Base URL prefix (e.g. https://example.com)' v-model='loadTest.baseUrl' type='text')
                | Inputs
                bionic-metrics-monaco-simple-editor.inline-editor(
                  ref='editor'
                  type='input'
                  git-name='jsonInputs.json'
                  @input='triggerSave'
                  v-model='loadTest.json'
                  mode='json')
          v-tab-item(key='concurrency')
            v-card(flat tile)
              v-card-text
                v-slider.mt-4(@change='triggerSave'
                  v-model='loadTest.concurrency'
                  min='1'
                  max='100'
                  thumb-label
                  label='Concurrency'
                  persistent-hint
                  hint='Number of threads to run in parallel')
                  template(v-slot:append)
                    v-text-field.mt-0.pt-0(v-model='loadTest.concurrency' @input='triggerSave' type='number' style='width: 60px' min='1' max='100')
                v-divider.mt-5
                v-row
                  v-col(cols='4' sm='4' md='2').text-body-2 Concurrent Users:
                  v-col {{total}}
                v-row
                  v-col(cols='4' sm='4' md='2').text-body-2 Required Number of Agents:
                  v-col {{machinedNeeded}}
          v-tab-item(key='tests')
            v-card(flat tile)
              bionic-metrics-load-test-list(v-model='loadTest.tests' @input='triggerSave')
      v-card.mb-2
        v-card-title
          .text-h5.font-weight-light Runs
          v-spacer
          bionic-metrics-load-test-run-button(:loadTest='loadTest')
        bionic-metrics-load-test-runs(:guid='guid')
</template>

<script>
import {sumBy} from 'lodash';
import {mapMutations} from 'vuex';
import BionicMetricsPageLoading from '@/components/PageLoading';
import LoadTest from '@/api/loadTest';
import BionicMetricsMonacoSimpleEditor from '@/components/MonacoSimpleEditor';
import Notifications from '@/services/notification.service';
import SaveMixin from '@/mixins/save.mixin';
import BionicMetricsLoadTestList from '@/components/LoadTestList';
import BionicMetricsLoadTestRuns from '@/components/LoadTestRuns';
import BionicMetricsLoadTestRunButton from '../../components/LoadTestRunButton';

export default {
  name: 'LoadTest.vue',
  mixins: [SaveMixin],
  components: {
    BionicMetricsLoadTestRunButton,
    BionicMetricsLoadTestRuns,
    BionicMetricsLoadTestList,
    BionicMetricsMonacoSimpleEditor,
    BionicMetricsPageLoading
  },
  props: {
    guid: {
      required: true,
      type: String
    }
  },
  async mounted() {
    await this.load();
  },
  computed: {
    saveObject() {
      return this.loadTest;
    },
    total() {
      return sumBy(this.loadTest.tests, 'count');
    },
    machinedNeeded() {
      if (!this.loadTest || !this.loadTest.concurrency) return 0;
      return Math.ceil(this.total / this.loadTest.concurrency);
    }
  },
  methods: {
    async load() {
      try {
        this.loading = true;
        this.loadTest = await LoadTest.get(this.guid);
      } catch (error) {
        return this.abort();
      } finally {
        this.loading = false;
      }
    },
    async save(loadTest) {
      try {
        await LoadTest.update(loadTest.guid, loadTest);
        Notifications.toast('Save Successful');
      } catch (error) {
        console.error(`Error saving: ${error.message}`);
        Notifications.errorToast('There was a problem saving', {duration: 30000, retry: this.save});
      }
    },
    updateBreadcrumb() {
      this.setBreadcrumb([{text: 'Load Tests', to: '/load-tests'}, {text: this.loadTest.name}]);
    },
    ...mapMutations({
      setBreadcrumb: 'setBreadcrumb'
    })
  },
  watch: {
    'loadTest.name': {
      handler: function () { // eslint-disable-line
        this.updateBreadcrumb();
      },
      deep: true
    }
  },
  data() {
    return {
      tab: 'details',
      loading: false,
      loadTest: null
    };
  }
};
</script>

<style scoped>
.inline-editor {
  width: 100%;
  min-height: 20vh;
}
</style>
